import type {
  PartialPricingEngineOverrides,
  OperationCellData,
  DisplayContext,
  OperationData,
} from '@frontend/api';

import {
  getCellsForOperation,
  getComponent as getComponentFromStore,
  getOperation as getOperationFromStore,
} from 'modules/quotes/quoteItems/data/selectors';

import { PAGE_REQUEST } from 'store/app/types';
import type { RootState } from 'store/configureStore';
import { ActionWithPayload } from 'store/types';

import { types } from './actions';

type OperationModalState = {
  cells: OperationCellData[];
  componentId: number | null;
  displayContext: DisplayContext[];
  isOpen: boolean;
  isReadOnly: boolean;
  operationId: number | null;
  overrides: PartialPricingEngineOverrides;
  liveOperation: OperationData | null;
};

const initialState: OperationModalState = {
  cells: [],
  componentId: null,
  displayContext: [],
  isOpen: false,
  isReadOnly: false,
  operationId: null,
  overrides: {},
  liveOperation: null,
};

const reducer = (
  state = initialState,
  action: ActionWithPayload<string, any>,
) => {
  const { payload, type } = action;
  switch (type) {
    case PAGE_REQUEST:
      return {
        ...initialState,
      };
    case types.OPEN_OPERATION_MODAL:
      return {
        ...state,
        isOpen: true,
        isReadOnly: payload.isReadOnly,
        operationId: payload.operation.id,
        componentId: payload.operation.componentId,
        displayContext: payload.operation.displayContext,
        overrides: payload.operation.overrides,
        cells: payload.cells,
      };
    case types.CLOSE_OPERATION_MODAL:
      return {
        ...state,
        isOpen: false,
        operation: null,
        operationId: null,
        componentId: null,
        displayContext: [],
        overrides: {},
        liveOperation: null,
      };
    case types.LIVE_PRICING_PREVIEW_REQUEST:
      return {
        ...state,
        overrides: payload.overrides,
      };
    case types.LIVE_PRICING_PREVIEW_SUCCESS:
      return {
        ...state,
        cells: payload.cells,
        displayContext: payload.displayContext,
        liveOperation: payload,
      };
    case types.RECEIVE_OVERRIDES:
      return {
        ...state,
        overrides: payload.nextOverrides,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): OperationModalState =>
  state.modules.quotes.operationModal;

export const getIsOperationModalOpen = (state: RootState) =>
  getScope(state).isOpen;

export const getOperationId = (state: RootState) => getScope(state).operationId;

export const getOperation = (state: RootState) => {
  const operationId = getOperationId(state);
  return operationId ? getOperationFromStore(state, operationId) : undefined;
};

export const getComponentId = (state: RootState) => getScope(state).componentId;

export const getComponent = (state: RootState) => {
  const componentId = getComponentId(state);
  return componentId ? getComponentFromStore(state, componentId) : undefined;
};

export const getSelectedComponentId = (state: RootState) =>
  getScope(state).componentId;

export const getCurrentOverrides = (state: RootState) =>
  getScope(state).overrides;

export const getLocalCells = (state: RootState) => getScope(state).cells;

export const getGlobalCells = (state: RootState) => {
  const operationId = getOperationId(state);
  return operationId ? getCellsForOperation(state, operationId) : [];
};

export const getIsReadOnly = (state: RootState) => getScope(state).isReadOnly;

export const getDisplayContext = (state: RootState) =>
  getScope(state).displayContext;

export const getLiveOperation = (state: RootState) =>
  getScope(state).liveOperation;
